import { useAuth0 } from '@auth0/auth0-react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';

import { getAuth0Config } from '../auth';
import { authedAtTimestampAtom, userTypeAtom } from '../auth/state';
import { gracefullySetLoggerGlobalContext } from '../datadog/logger';
import { ErrorPage } from '../error/ErrorPage';
import { useGetPublicProvider } from '../hooks/useGetPublicProvider';
import { useIsZoomEnabledCallback } from '../hooks/useIsZoomEnabled';
import { loadSegment } from '../segment/segment';
import { useGracefullyUpdateStatsigUserCallback } from '../statsig/useGracefullyUpdateStatsigUserCallback';
import { Provider, UserType } from '../types';
import { useInitializeVideoSDKAdapter } from '../video-sdk/actions/useLifecycleActions';
import { ProviderProtected, ProviderProtectedProps } from './ProviderProtected';
import { useProviderLoginCallback } from './auth/useProviderLoginCallback';
import { userDataAtom } from './state';
import { useGetCurrentProviderQueryCallback } from './useGetCurrentProviderQuery';

/**
 * Returns a callback that handles an authorized provider before the protected
 * app is rendered.
 */
function useHandleSuccessfulAuthCallback() {
  const gracefullyUpdateStatsigUser = useGracefullyUpdateStatsigUserCallback();
  const getPublicProvider = useGetPublicProvider();
  const setAuthedAt = useSetAtom(authedAtTimestampAtom);
  const setUserData = useSetAtom(userDataAtom);
  const initializeVideoSDKAdapter = useInitializeVideoSDKAdapter();
  const checkZoomIsEnabled = useIsZoomEnabledCallback();
  const [error, setError] = useState<Error>();

  const handler = useCallback(
    async (provider: Required<Provider>) => {
      // initialize Statsig with a Provider's State of Operation to allow more
      // targeted rollout of Zoom vs. Twilio
      const { data: publicProvider } = await getPublicProvider(provider.shortId);

      await gracefullyUpdateStatsigUser({
        userID: provider.shortId,
        customIDs: {
          providerShortID: provider.shortId,
        },
        custom: {
          providerUsState: publicProvider?.state || 'UNKNOWN',
          userType: UserType.PROVIDER,
        },
      });
      gracefullySetLoggerGlobalContext({ providerShortId: provider.shortId });

      loadSegment(UserType.PROVIDER, provider.shortId);
      setUserData(provider);

      // Video SDK initialization error should be blocking
      await initializeVideoSDKAdapter(checkZoomIsEnabled() ? 'zoom' : 'twilio').catch((err: Error) => {
        setError(err);
      });

      // This will trigger the protected app to render
      setAuthedAt(Date.now());
    },
    [
      checkZoomIsEnabled,
      gracefullyUpdateStatsigUser,
      initializeVideoSDKAdapter,
      setAuthedAt,
      setUserData,
      getPublicProvider,
    ],
  );

  return [
    handler,
    {
      error,
    },
  ] as const;
}

export function ProviderProtectedWrapper({ children }: Pick<ProviderProtectedProps, 'children'>) {
  const [getCurrentProvider] = useGetCurrentProviderQueryCallback();
  const { isLoading: isLoadingAuth, logout, isAuthenticated } = useAuth0();
  const [handleSuccessfulAuth, { error }] = useHandleSuccessfulAuthCallback();
  const hasAuthedBefore = !!useAtomValue(authedAtTimestampAtom);
  const setUserType = useSetAtom(userTypeAtom);
  const handleLogin = useProviderLoginCallback();

  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <ProviderProtected
      {...{
        auth0Config: getAuth0Config(),
        isAuthenticated,
        getCurrentProvider,
        isLoadingAuth,
        logout,
        onAuthSuccess: handleSuccessfulAuth,
        hasAuthedBefore,
        setUserType,
        handleLogin,
      }}
    >
      {children}
    </ProviderProtected>
  );
}
